import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchAllUsers, 
  getDashboardStats, 
  verifyUser, 
  unverifyUser, 
  banUser, 
  unbanUser,
  makeAdmin,
  removeAdmin,
  // changeNicknameColor,
  // addBadge,
  // removeBadge,
  deleteUser
} from '../redux/adminSlice';
import axios from 'axios';
import '../styles/Admin.css';

const Admin = () => {
  const dispatch = useDispatch();
  const { users, stats, loading } = useSelector(state => state.admin);
  const { user: currentUser } = useSelector(state => state.auth);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [activeTab, setActiveTab] = useState('users');
  
  const [updateForm, setUpdateForm] = useState({
    title: '',
    content: '',
    version: '',
    priority: 'medium',
    image: null
  });
  const [updates, setUpdates] = useState([]);
  
  const [customizeUser, setCustomizeUser] = useState(null);
  const [customizeForm, setCustomizeForm] = useState({
    nicknameColor: '#ffffff',
    badge: '',
    icon: null,
    makeAdmin: false
  });

  useEffect(() => {
    dispatch(fetchAllUsers());
    dispatch(getDashboardStats());
    loadUpdates();
  }, [dispatch]);

  const loadUpdates = async () => {
    try {
      const response = await axios.get('http://localhost:5000/api/updates');
      setUpdates(response.data);
    } catch (error) {
      console.error('Error loading updates:', error);
    }
  };

  const handleVerify = (userId) => {
    dispatch(verifyUser(userId));
  };

  const handleUnverify = (userId) => {
    dispatch(unverifyUser(userId));
  };

  const handleBan = (userId) => {
    dispatch(banUser(userId));
  };

  const handleUnban = (userId) => {
    dispatch(unbanUser(userId));
  };

  const handleDeleteUser = (userId) => {
    if (window.confirm('Delete this user and all their posts?')) {
      dispatch(deleteUser(userId));
    }
  };

  const handleCreateUpdate = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('title', updateForm.title);
      formData.append('content', updateForm.content);
      formData.append('version', updateForm.version);
      formData.append('priority', updateForm.priority);
      if (updateForm.image) {
        formData.append('image', updateForm.image);
      }

      await axios.post(
        'http://localhost:5000/api/updates',
        formData,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
        }
      );

      setUpdateForm({
        title: '',
        content: '',
        version: '',
        priority: 'medium',
        image: null
      });
      loadUpdates();
      alert('Update created successfully!');
    } catch (error) {
      console.error('Error creating update:', error);
      alert('Error creating update');
    }
  };

  const handleDeleteUpdate = async (updateId) => {
    if (window.confirm('Delete this update?')) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(
          `http://localhost:5000/api/updates/${updateId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        loadUpdates();
      } catch (error) {
        console.error('Error deleting update:', error);
      }
    }
  };

  const handleCustomizeUser = (user) => {
    setCustomizeUser(user);
    setCustomizeForm({
      nicknameColor: user.nicknameColor || '#ffffff',
      badge: user.badge || '',
      icon: null,
      makeAdmin: user.isAdmin
    });
  };

  const handleSaveCustomization = async () => {
    try {
      const token = localStorage.getItem('token');

      if (customizeForm.nicknameColor !== customizeUser?.nicknameColor) {
        await axios.patch(
          `http://localhost:5000/api/admin/${customizeUser._id}/nickname-color`,
          { color: customizeForm.nicknameColor },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      if (customizeForm.badge !== customizeUser?.badge) {
        if (customizeForm.badge) {
          await axios.patch(
            `http://localhost:5000/api/admin/${customizeUser._id}/badge`,
            { badge: customizeForm.badge },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } else if (customizeUser?.badge) {
          await axios.delete(
            `http://localhost:5000/api/admin/${customizeUser._id}/badge`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
        }
      }

      if (customizeForm.icon) {
        const iconFormData = new FormData();
        iconFormData.append('icon', customizeForm.icon);
        await axios.post(
          `http://localhost:5000/api/admin/${customizeUser._id}/icon`,
          iconFormData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      if (customizeForm.makeAdmin !== customizeUser?.isAdmin) {
        if (customizeForm.makeAdmin) {
          dispatch(makeAdmin(customizeUser._id));
        } else {
          dispatch(removeAdmin(customizeUser._id));
        }
      }

      setCustomizeUser(null);
      dispatch(fetchAllUsers());
      alert('User customization saved!');
    } catch (error) {
      console.error('Error saving customization:', error);
      alert('Error saving changes');
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch =
      user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      `${user.firstName} ${user.lastName}`.toLowerCase().includes(searchTerm.toLowerCase());

    if (filterStatus === 'verified') return matchesSearch && user.isVerified;
    if (filterStatus === 'banned') return matchesSearch && !user.isActive;
    if (filterStatus === 'active') return matchesSearch && user.isActive;
    if (filterStatus === 'admin') return matchesSearch && user.isAdmin;
    return matchesSearch;
  });

  const isCreator = currentUser?.username === process.env.REACT_APP_CREATOR_USERNAME;

  if (!currentUser?.isAdmin) {
    return (
      <div className="admin-container">
        <div className="admin-error">
          <h2>❌ Access Denied</h2>
          <p>You are not an administrator</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>🎛️ Admin Dashboard</h1>
        <p>
          {isCreator ? '👑 Creator Mode - All functions available' : '⚙️ Admin Mode - Limited functions'}
        </p>
      </div>

      <div className="admin-tabs">
        <button
          className={`tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          👥 Users Management
        </button>
        <button
          className={`tab ${activeTab === 'updates' ? 'active' : ''}`}
          onClick={() => setActiveTab('updates')}
        >
          📢 Updates
        </button>
        {isCreator && (
          <button
            className={`tab ${activeTab === 'customize' ? 'active' : ''}`}
            onClick={() => setActiveTab('customize')}
          >
            ✨ Customize Users
          </button>
        )}
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card total">
            <div className="stat-icon">👥</div>
            <div className="stat-content">
              <div className="stat-value">{stats.totalUsers}</div>
              <div className="stat-label">Total Users</div>
            </div>
          </div>
          <div className="stat-card verified">
            <div className="stat-icon">✅</div>
            <div className="stat-content">
              <div className="stat-value">{stats.verifiedUsers}</div>
              <div className="stat-label">Verified</div>
            </div>
          </div>
          <div className="stat-card active">
            <div className="stat-icon">🟢</div>
            <div className="stat-content">
              <div className="stat-value">{stats.activeUsers}</div>
              <div className="stat-label">Active</div>
            </div>
          </div>
          <div className="stat-card banned">
            <div className="stat-icon">🚫</div>
            <div className="stat-content">
              <div className="stat-value">{stats.bannedUsers}</div>
              <div className="stat-label">Banned</div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'users' && (
        <div className="tab-content">
          <div className="admin-controls">
            <input
              type="text"
              placeholder="🔍 Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Users</option>
              <option value="verified">Verified Only</option>
              <option value="active">Active Only</option>
              <option value="banned">Banned Only</option>
              <option value="admin">Admins Only</option>
            </select>
          </div>

          <div className="users-section">
            <h2>Users Management</h2>
            {loading ? (
              <div className="loading">Loading users...</div>
            ) : (
              <div className="users-table-container">
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Email</th>
                      <th>Status</th>
                      <th>Role</th>
                      <th>Followers</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map(user => (
                      <tr key={user._id} className={`user-row ${!user.isActive ? 'banned' : ''}`}>
                        <td className="user-col">
                          <div className="user-info">
                            <div className="user-avatar">
                              {user.icon ? (
                                <img
                                  src={`http://localhost:5000${user.icon}`}
                                  alt="icon"
                                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                                />
                              ) : (
                                <>{user.firstName?.charAt(0)}{user.lastName?.charAt(0)}</>
                              )}
                            </div>
                            <div>
                              <div className="user-name">
                                {user.firstName} {user.lastName}
                                {user.isVerified && <span className="verify-badge">✅</span>}
                                {user.badge && <span className="badge-display">{user.badge}</span>}
                              </div>
                              <div className="user-username" style={{ color: user.nicknameColor || '#666' }}>@{user.username}</div>
                            </div>
                          </div>
                        </td>
                        <td className="text-small">{user.email}</td>
                        <td>
                          <span className={`status-badge ${user.isActive ? 'active' : 'banned'}`}>
                            {user.isActive ? '🟢 Active' : '🔴 Banned'}
                          </span>
                        </td>
                        <td>
                          {user.isAdmin ? (
                            <span className="role-badge admin">👑 Admin</span>
                          ) : (
                            <span className="role-badge user">👤 User</span>
                          )}
                        </td>
                        <td className="text-center">{user.followers?.length || 0}</td>
                        <td className="actions-col">
                          <div className="action-buttons">
                            {user.isVerified ? (
                              <button
                                className="btn btn-sm btn-unverify"
                                onClick={() => handleUnverify(user._id)}
                                title="Unverify"
                              >
                                Remove ✓
                              </button>
                            ) : (
                              <button
                                className="btn btn-sm btn-verify"
                                onClick={() => handleVerify(user._id)}
                                title="Verify"
                              >
                                Verify
                              </button>
                            )}
                            {user.isActive ? (
                              <button
                                className="btn btn-sm btn-ban"
                                onClick={() => handleBan(user._id)}
                                title="Ban"
                              >
                                🚫 Ban
                              </button>
                            ) : (
                              <button
                                className="btn btn-sm btn-unban"
                                onClick={() => handleUnban(user._id)}
                                title="Unban"
                              >
                                ✓ Unban
                              </button>
                            )}
                            {isCreator && (
                              <>
                                <button
                                  className="btn btn-sm btn-customize"
                                  onClick={() => handleCustomizeUser(user)}
                                  title="Customize"
                                >
                                  ✨ Edit
                                </button>
                                <button
                                  className="btn btn-sm btn-delete"
                                  onClick={() => handleDeleteUser(user._id)}
                                  title="Delete user and posts"
                                >
                                  🗑️ Delete
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'updates' && (
        <div className="tab-content">
          <div className="updates-section">
            <div className="updates-create">
              <h2>📢 Create Update</h2>
              <form onSubmit={handleCreateUpdate} className="update-form">
                <input
                  type="text"
                  placeholder="Update Title"
                  value={updateForm.title}
                  onChange={(e) => setUpdateForm({ ...updateForm, title: e.target.value })}
                  required
                />
                <textarea
                  placeholder="Update Content / Description"
                  value={updateForm.content}
                  onChange={(e) => setUpdateForm({ ...updateForm, content: e.target.value })}
                  rows="4"
                  required
                />
                <div className="form-row">
                  <input
                    type="text"
                    placeholder="Version (e.g., 1.2.0)"
                    value={updateForm.version}
                    onChange={(e) => setUpdateForm({ ...updateForm, version: e.target.value })}
                  />
                  <select
                    value={updateForm.priority}
                    onChange={(e) => setUpdateForm({ ...updateForm, priority: e.target.value })}
                  >
                    <option value="low">🟢 Low Priority</option>
                    <option value="medium">🟡 Medium Priority</option>
                    <option value="high">🟠 High Priority</option>
                    <option value="critical">🔴 Critical Priority</option>
                  </select>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setUpdateForm({ ...updateForm, image: e.target.files[0] })}
                />
                <button type="submit" className="btn btn-primary">
                  📢 Publish Update
                </button>
              </form>
            </div>

            <div className="updates-list">
              <h2>📋 Recent Updates</h2>
              {updates.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#888', padding: '2rem' }}>
                  No updates yet
                </div>
              ) : (
                updates.map(update => (
                  <div key={update._id} className="update-card">
                    <div className="update-header">
                      <h3>{update.title}</h3>
                      <span className={`priority-badge ${update.priority}`}>
                        {update.priority.toUpperCase()}
                      </span>
                    </div>
                    {update.image && (
                      <img
                        src={`http://localhost:5000${update.image}`}
                        alt={update.title}
                        className="update-image-preview"
                      />
                    )}
                    <p>{update.content}</p>
                    {update.version && <small>Version: {update.version}</small>}
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDeleteUpdate(update._id)}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'customize' && isCreator && (
        <div className="tab-content">
          {!customizeUser ? (
            <div className="customize-section">
              <h2>✨ Customize Users</h2>
              <p style={{ color: '#888', marginBottom: '2rem' }}>
                Select a user from Users tab and click "✨ Edit" button
              </p>
              <div className="info-box">
                <h3>Available Customizations:</h3>
                <ul>
                  <li><strong>Nickname Color:</strong> Change the color of their name (HEX format)</li>
                  <li><strong>Badge:</strong> Add emoji next to name (👑, 🏆, ⭐, etc.)</li>
                  <li><strong>Icon Image:</strong> Upload custom icon image (JPEG, PNG, GIF)</li>
                  <li><strong>Admin Status:</strong> Make them admin or remove rights</li>
                </ul>
              </div>
            </div>
          ) : (
            <div className="customize-form-section">
              <h2>Customize: {customizeUser.firstName} {customizeUser.lastName}</h2>
              <button
                className="btn btn-secondary"
                onClick={() => setCustomizeUser(null)}
                style={{ marginBottom: '1rem' }}
              >
                ← Back
              </button>

              <div className="customize-card">
                <div className="customize-preview">
                  <div style={{
                    padding: '1rem',
                    background: 'rgba(29, 209, 161, 0.1)',
                    borderRadius: '8px',
                    marginBottom: '1rem'
                  }}>
                    <h3>Preview</h3>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem'
                    }}>
                      {customizeUser.icon && (
                        <img
                          src={`http://localhost:5000${customizeUser.icon}`}
                          alt="icon"
                          style={{
                            width: '30px',
                            height: '30px',
                            borderRadius: '50%',
                            objectFit: 'cover'
                          }}
                        />
                      )}
                      <div style={{
                        color: customizeForm.nicknameColor,
                        fontSize: '1.3rem',
                        fontWeight: 'bold',
                        display: 'flex',
                        gap: '0.5rem',
                        alignItems: 'center'
                      }}>
                        {customizeUser.firstName} {customizeUser.lastName}
                        {customizeForm.badge && <span>{customizeForm.badge}</span>}
                      </div>
                    </div>
                  </div>
                </div>

                <form onSubmit={(e) => { e.preventDefault(); handleSaveCustomization(); }}>
                  <div className="form-group">
                    <label>Nickname Color (HEX)</label>
                    <div className="color-input-group">
                      <input
                        type="text"
                        placeholder="#FF5733"
                        value={customizeForm.nicknameColor}
                        onChange={(e) => setCustomizeForm({ ...customizeForm, nicknameColor: e.target.value })}
                        maxLength="7"
                      />
                      <input
                        type="color"
                        value={customizeForm.nicknameColor}
                        onChange={(e) => setCustomizeForm({ ...customizeForm, nicknameColor: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Badge/Icon (emoji or text)</label>
                    <input
                      type="text"
                      placeholder="👑, 🏆, ⭐, etc."
                      value={customizeForm.badge}
                      onChange={(e) => setCustomizeForm({ ...customizeForm, badge: e.target.value })}
                      maxLength="5"
                    />
                  </div>

                  <div className="form-group">
                    <label>Upload Icon Image</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setCustomizeForm({ ...customizeForm, icon: e.target.files[0] })}
                    />
                    {customizeUser.icon && (
                      <small style={{ color: '#1dd1a1', marginTop: '0.5rem', display: 'block' }}>
                        ✓ Current icon: {customizeUser.icon.split('/').pop()}
                      </small>
                    )}
                  </div>

                  <div className="form-group checkbox">
                    <label className="switch-label">
                      <input
                        type="checkbox"
                        checked={customizeForm.makeAdmin}
                        onChange={(e) => setCustomizeForm({ ...customizeForm, makeAdmin: e.target.checked })}
                        className="switch-checkbox"
                      />
                      <span className="switch-slider"></span>
                      <span className="switch-text">Make Admin</span>
                    </label>
                  </div>

                  <button type="submit" className="btn btn-primary">
                    ✅ Save Changes
                  </button>
                </form>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Admin;
